import React from 'react';
import classnames from 'classnames';
import { MessageItemProps } from '../interfaces';
import { renderMediaContent, MessageReplyPreview } from './MediaAttachments';
import { AgentBadge } from '../AgentBadge';
import { formatMessageTime, formatSeparatorDate } from "../../utils/date.util";



const MessageItem: React.FC<MessageItemProps> = ({
    message,
    isSentByMe,
    isSameDirectionAsPrev,
    isLastInClosedConv,
    showBadge,
    isInputDisabled,
    selectedContact,
    handleInitiateReply,
    handleReplyClick,
    resendMessage,
    hasPrevMessage,
    messages,
    getMediaUrl
}) => {
    // Definimos si es sticker fuera del return para limpiar el código
    const isSticker = message.type === 'sticker';
    return (
        <React.Fragment key={message.id}>
            <li
                id={`msg-${message.id}`}
                className={classnames('chat-list', {
                    right: isSentByMe,
                    left: !isSentByMe,
                    'mt-1': isSameDirectionAsPrev,
                    'mt-4': !isSameDirectionAsPrev && hasPrevMessage,
                })}
            >
                <div className="conversation-list">
                    <div className="user-chat-content flex-grow-1"
                         style={{minWidth: 0}}>
                        <div className="ctext-wrap">
                            <div
                                className="d-flex align-items-center gap-2 message-hover-container">
                                {/* Reply button for outbound (left side) */}
                                {isSentByMe && !message.isPending && !message.isFailed && !isInputDisabled && (
                                    <button
                                        className="btn btn-sm btn-link text-muted p-1 message-action-btn flex-shrink-0"
                                        onClick={() => handleInitiateReply(message)}
                                        title="Responder"
                                    >
                                        <i className="ri-reply-line fs-16"></i>
                                    </button>
                                )}
                                {isSentByMe && message.isPending && (
                                    <div
                                        className="spinner-border text-muted mb-0 flex-shrink-0"
                                        role="status" style={{
                                        width: '1rem',
                                        height: '1rem',
                                        borderWidth: '0.15em'
                                    }}>
                                        <span
                                            className="visually-hidden">Enviando...</span>
                                    </div>
                                )}
                                {isSentByMe && message.isFailed && (
                                    <button
                                        className="btn btn-sm btn-link text-danger p-0 flex-shrink-0"
                                        onClick={() => resendMessage(message.id, message.content)}
                                        title="Reintentar envío"
                                    >
                                        <i className="ri-refresh-line fs-16"></i>
                                    </button>
                                )}

                                <div
                                    className="d-flex flex-column align-items-end"
                                    style={{maxWidth: '100%'}}>
                                    {!message.isPending && showBadge && (
                                        <div
                                            className="mb-1 align-self-end">
                                            <AgentBadge
                                                agentName={message.agent?.name}/>
                                        </div>
                                    )}

                                    <div
                                        className={classnames(
                                            'ctext-wrap-content',
                                            'position-relative',
                                            {
                                                'd-flex flex-column text-white shadow-sm': !isSticker && isSentByMe,
                                                'd-flex flex-column shadow-sm': !isSticker && !isSentByMe,
                                                'relative inline-block p-0 bg-transparent shadow-none border-0': isSticker,
                                            }
                                        )}
                                        style={{
                                            backgroundColor: isSticker
                                                ? 'transparent'
                                                : (isSentByMe ? '#075E54' : '#ffffff'),
                                            opacity: (isSentByMe && (message.isPending || message.isFailed)) ? 0.7 : 1,
                                            minWidth: isSticker ? 'auto' : '80px', // Evitamos que el sticker se estire innecesariamente
                                        }}
                                    >
                                        {/* Reply reference block */}
                                        {message.replyMessage && (
                                            <div
                                                className="p-1 mb-1 rounded reply-reference"
                                                style={{
                                                    backgroundColor: isSentByMe ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                                    borderLeft: `4px solid ${isSentByMe ? '#25D366' : '#075E54'}`,
                                                    fontSize: '0.85em',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleReplyClick(message)}
                                            >
                                                <div
                                                    className="reply-author mb-1"
                                                    style={{fontWeight: 600}}>
                                                    {message.replyMessage.direction === 'outbound'
                                                        ? 'Tú'
                                                        : (selectedContact?.name || 'Contacto')
                                                    }
                                                </div>
                                                <div
                                                    className="reply-content-truncate"
                                                    style={{opacity: 0.9}}>
                                                    <MessageReplyPreview message={message.replyMessage as any} />
                                                </div>
                                            </div>
                                        )}
                                        {/* Contenido normal para Texto, Documento, Imagen, Audio, etc. */}
                                        {!isSticker && (
                                            <div className="d-flex flex-wrap align-items-end" style={{ gap: '0.25rem 0.5rem' }}>
                                                <div className="ctext-content flex-grow-1" style={{ whiteSpace: 'pre-wrap', minWidth: 0, overflowWrap: 'anywhere' }}>
                                                    {renderMediaContent(message, {
                                                        messages,
                                                        currentMessageId: message.id,
                                                        getMediaUrl
                                                    })}
                                                </div>
                                                {/* Metadatos (Hora/Checks) para burbujas normales */}
                                                <div className="message-metadata d-flex align-items-center ms-auto flex-shrink-0">
                                                    <span>{formatMessageTime(message.createdAt)}</span>
                                                    {isSentByMe && (
                                                        <i className="ri-check-double-line ms-1"></i>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Contenido simplificado para Sticker */}
                                        {isSticker && (
                                            <div className="relative overflow-visible leading-[0]">
                                                {renderMediaContent(message, {
                                                    messages,
                                                    currentMessageId: message.id,
                                                    getMediaUrl
                                                })}
                                            </div>
                                        )}
                                        {message.reaction && message.reaction && (
                                            <div
                                                className="position-absolute bg-white rounded-pill shadow-sm border border-light d-flex align-items-center justify-content-center"
                                                style={{
                                                    bottom: '-12px',
                                                    fontSize: '13px',
                                                    zIndex: 5,
                                                    minWidth: '28px',
                                                    height: '24px'
                                                }}
                                                title="Reacción"
                                            >
                                                {message.reaction}
                                            </div>
                                        )}
                                        </div>
                                </div>
                                {/* Reply button for inbound (right side) */}
                                {!isSentByMe && !isInputDisabled && (
                                    <button
                                        className="btn btn-sm btn-link text-muted p-1 message-action-btn flex-shrink-0"
                                        onClick={() => handleInitiateReply(message)}
                                        title="Responder"
                                    >
                                        <i className="ri-reply-line fs-16"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </li>

            {/* Closure separator */}
            {isLastInClosedConv && (
                <div className="chat-list-separator my-3 text-center">
                    <div
                        className="d-flex align-items-center justify-content-center">
                        <hr className="flex-grow-1 mx-2"/>
                        <span
                            className="px-3 py-1 rounded-pill fs-11 text-muted fw-semibold shadow-sm"
                            style={{background: 'rgba(255,255,255,0.85)'}}>
                        <i className="ri-error-warning-line me-1 text-warning"></i>
                        Conversación cerrada el {formatSeparatorDate(message.conversationClosedAt)}
                    </span>
                        <hr className="flex-grow-1 mx-2"/>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default MessageItem;
